import classNames from "classnames";
import { DateTime } from "luxon";
import QRCode from "qrcode.react";

import { LISCase, LISCaseList } from "pwapi";

import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { useAppSelector } from "app/hooks";
import { useSelectedLab } from "hooks/useSelectedLabStation";
import { truncatePatientName } from "util/patientUtils";
import { labelForSlide, stainTemplate } from "util/slideUtils";

const SlideBarcode = ({
  id,
  printCase,
  printer,
}: {
  id: Number;
  printCase: LISCase | LISCaseList;
  printer?: string | null;
}) => {
  const lab = useSelectedLab();
  const slide = printCase?.slides.find((s) => s.id === id);
  const stain = useAppSelector(({ stains }) => stains[slide?.stain_id || 0]);
  const antigenRetrievalMethods = useAppSelector(
    ({ antigenRetrievalMethods }) => antigenRetrievalMethods
  );
  const antibodySpecies = useAppSelector(
    ({ antibodySpecies }) => antibodySpecies
  );
  const clinic = useAppSelector(({ clinics }) =>
    printCase?.patient?.clinic_id
      ? clinics[printCase?.patient?.clinic_id || 0]
      : undefined
  );

  const increasedSlideECC = useAppSelector((state) =>
    selectFeatureFlag(state, "increasedSlideECC")
  );
  const nonSquareZebraPrintFlag = useAppSelector((state) =>
    selectFeatureFlag(state, "nonSquareZebraPrint")
  );
  const slidePrintFitLabelWidthFlag = useAppSelector((state) =>
    selectFeatureFlag(state, "slidePrintFitLabelWidth")
  );
  const ihcSlideReorganizationFlag = useAppSelector((state) =>
    selectFeatureFlag(state, "ihcSlideReorganization")
  );

  const ihcSlideReorganization =
    ihcSlideReorganizationFlag ||
    (lab.settings?.ihc_slide_reorganization?.active &&
      Array.isArray(lab.settings?.ihc_slide_reorganization?.details) &&
      lab.settings?.ihc_slide_reorganization?.details.includes(stain.category));
  const nonSquareZebraPrint =
    nonSquareZebraPrintFlag ||
    lab.settings?.non_square_zebra_print?.active ||
    ihcSlideReorganization; // nonSquareZebraPrint is a dependency for ihcSlideReorganization
  const slidePrintFitLabelWidth =
    slidePrintFitLabelWidthFlag ||
    lab.settings?.slide_print_fit_label_width?.active ||
    ihcSlideReorganization; // slidePrintFitLabelWidth is a dependency for ihcSlideReorganization
  const hardSpaceAfterRetrieval =
    lab.settings?.hard_space_after_retrieval?.active;

  const antigen_retrieval_method = slide?.antigen_retrieval_method_id
    ? antigenRetrievalMethods[slide.antigen_retrieval_method_id]
    : undefined;
  const antibody_species = slide?.antibody_species_id
    ? antibodySpecies[slide.antibody_species_id]
    : undefined;

  const patientName = truncatePatientName(printCase?.patient);

  const dermGroupGlassWorkflow =
    lab.settings?.derm_group_assignment?.active && printCase?.glass_read;

  // When to print a compressed slide (smaller font size to accomodate more data)
  const compressedSlide =
    dermGroupGlassWorkflow ||
    (lab.settings?.ihc_slide_labels?.active &&
      (antigen_retrieval_method || antibody_species?.label)) ||
    lab.settings?.force_compressed_slides?.active ||
    ihcSlideReorganization;

  return slide ? (
    <div
      className={classNames(
        "DecoratedBarcode",
        "DecoratedBarcode--slide",
        `DecoratedBarcode--${printer}`,
        {
          "DecoratedBarcode--zebraPrintChanges":
            printer === "zebra" && lab.settings?.zebra_print_changes?.active,
          "DecoratedBarcode--espoPrintChanges":
            printer === "espo" && lab.settings?.espo_print_changes?.active,
          "DecoratedBarcode--nonSquareZebraPrint":
            printer === "zebra" && nonSquareZebraPrint,
          "DecoratedBarcode--fitWidth": slidePrintFitLabelWidth,
          "DecoratedBarcode--ihcSlideReorganization": ihcSlideReorganization,
        }
      )}
      style={
        ihcSlideReorganization && lab.settings?.slide_style?.active
          ? (
              lab.settings?.slide_style?.details as {
                [key: string]: React.CSSProperties;
              }
            )?.DecoratedBarcode || undefined
          : undefined
      }
    >
      <div
        className={classNames("SlideBarcode", {
          "SlideBarcode--compressed": compressedSlide,
        })}
        style={
          ihcSlideReorganization && lab.settings?.slide_style?.active
            ? (
                lab.settings?.slide_style?.details as {
                  [key: string]: React.CSSProperties;
                }
              )?.SlideBarcode || undefined
            : undefined
        }
      >
        {ihcSlideReorganization ? (
          <>
            <div>
              <header>
                <p>
                  <strong>{printCase.accession}</strong>
                </p>
                {antigen_retrieval_method && (
                  <p>
                    {antigen_retrieval_method.abbreviation}
                    {hardSpaceAfterRetrieval && <>&nbsp;</>}
                  </p>
                )}
              </header>
              <header>
                <p>
                  {labelForSlide(slide, printCase)}
                  {slide.is_control && " CL"}
                </p>
              </header>
              <header>
                {stain && (
                  <p>
                    <strong>
                      {slide.label_override || stainTemplate(slide, stain)}
                      {compressedSlide && antibody_species?.label
                        ? antibody_species?.label
                        : null}
                    </strong>
                  </p>
                )}
              </header>
            </div>
            <figure>
              <QRCode
                value={`slide-${slide?.id}`}
                size={32}
                bgColor={"#ffffff"}
                fgColor={"var(--black)"}
                level={increasedSlideECC ? "H" : "L"}
                includeMargin={false}
                renderAs={"svg"}
              />
              <figcaption>
                {printCase?.patient?.last_name && (
                  <p>{printCase.patient.last_name.slice(0, 9)},</p>
                )}
                {printCase?.patient?.first_name && (
                  <p>{printCase.patient.first_name.slice(0, 9)}</p>
                )}
                {slide.created_at && (
                  <p>
                    {DateTime.fromISO(slide.created_at).toFormat("MM/dd/yy")}
                  </p>
                )}
                {dermGroupGlassWorkflow && (
                  <p className="SlideBarcode__distribution">
                    <strong>
                      {clinic?.location?.state}{" "}
                      {clinic?.clinic_derm_group?.label || "TBD"}
                    </strong>
                  </p>
                )}
              </figcaption>
            </figure>
          </>
        ) : (
          <>
            <div>
              <header>
                <p className="SlideBarcode__patient">
                  <strong>{printCase.accession}</strong>
                </p>
                {dermGroupGlassWorkflow && (
                  <p>
                    {!slidePrintFitLabelWidth && <>&nbsp;</>}
                    {clinic?.clinic_derm_group?.label || "TBD"}
                  </p>
                )}
              </header>
              <header>
                <p>{patientName}</p>
                {dermGroupGlassWorkflow && clinic?.location?.state && (
                  <p>
                    {!slidePrintFitLabelWidth && <>&nbsp;</>}
                    {clinic?.location?.state}
                  </p>
                )}
              </header>
            </div>
            <figure>
              <QRCode
                value={`slide-${slide?.id}`}
                size={32}
                bgColor={"#ffffff"}
                fgColor={"var(--black)"}
                level={increasedSlideECC ? "H" : "L"}
                includeMargin={false}
                renderAs={"svg"}
              />
              <figcaption>
                <p>{labelForSlide(slide, printCase)}</p>
                {stain && (
                  <p>
                    {stainTemplate(slide, stain)}
                    {compressedSlide && antibody_species?.label
                      ? antibody_species?.label
                      : null}
                  </p>
                )}
                {compressedSlide && antigen_retrieval_method && (
                  <p>{antigen_retrieval_method.abbreviation}</p>
                )}
                {slide.created_at && (
                  <p>
                    {DateTime.fromISO(slide.created_at).toFormat("MM/dd/yy")}
                  </p>
                )}
              </figcaption>
            </figure>
          </>
        )}
      </div>
    </div>
  ) : null;
};

export default SlideBarcode;
